import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Form, 
  Input, 
  Select, 
  Pagination, 
  Message, 
  Header, 
  Icon, 
  Label, 
  Modal, 
  TextArea,
  Statistic,
  Grid,
  Segment,
  Progress,
  Divider,
  Dropdown
} from 'semantic-ui-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { API, showError, showInfo, showSuccess, timestamp2string, isAdmin } from '../../helpers';
import useAdminList from '../../helpers/useAdminList';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const typeOptions = [
  { key: 'all', text: '全部类型', value: '' },
  { key: 'suggestion', text: '功能建议', value: 1 },
  { key: 'minor', text: '小问题', value: 2 },
  { key: 'urgent', text: '紧急问题', value: 3 }
];

const statusOptions = [
  { key: 'all', text: '全部状态', value: '' },
  { key: 'pending', text: '待处理', value: 1 },
  { key: 'processing', text: '处理中', value: 2 },
  { key: 'resolved', text: '已解决', value: 3 },
  { key: 'closed', text: '已关闭', value: 4 }
];

const priorityOptions = [
  { key: 'all', text: '全部优先级', value: '' },
  { key: 'low', text: '低', value: 1 },
  { key: 'medium', text: '中', value: 2 },
  { key: 'high', text: '高', value: 3 },
  { key: 'urgent', text: '紧急', value: 4 }
];

const FeedbackManagement = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [showStatistics, setShowStatistics] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: '',
    priority: '',
    admin_response: '',
    tags: ''
  });
  const [assignForm, setAssignForm] = useState({
    assigned_to: ''
  });
  
  // 使用管理员列表hook
  const { 
    adminList, 
    getAdminDisplayName, 
    getAdminFullInfo, 
    getAdminOptions, 
    getFilterOptions 
  } = useAdminList();

  const loadFeedbacks = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/feedbacks?page=${activePage}&page_size=${pageSize}`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (priorityFilter) url += `&priority=${priorityFilter}`;
      if (assignedToFilter) url += `&assigned_to=${assignedToFilter}`;
      
      const res = await API.get(url);
      const { success, message, data } = res.data;
      if (success) {
        const feedbacks = data.feedbacks || [];
        const total = data.total || 0;
        setFeedbacks(feedbacks);
        setTotal(total);
      } else {
        showError(message || '获取反馈列表失败');
        setFeedbacks([]);
        setTotal(0);
      }
    } catch (error) {
      showError('获取反馈列表失败: ' + error.message);
      setFeedbacks([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    try {
      const res = await API.get('/api/admin/feedbacks/statistics');
      const { success, message, data } = res.data;
      if (success) {
        setStatistics(data);
      } else {
        showError(message || '获取反馈统计失败');
      }
    } catch (error) {
      showError('获取反馈统计失败: ' + error.message);
    }
  };


  const handleSearch = async () => {
    setSearchLoading(true);
    setActivePage(1);
    await loadFeedbacks();
    setSearchLoading(false);
  };

  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  const showFeedbackDetails = async (feedbackId) => {
    try {
      const res = await API.get(`/api/admin/feedbacks/${feedbackId}`);
      const { success, message, data } = res.data;
      if (success) {
        setSelectedFeedback(data);
        setShowFeedbackModal(true);
      } else {
        showError(message || '加载反馈详情失败');
      }
    } catch (error) {
      showError('加载反馈详情失败: ' + error.message);
    }
  };

  const handleUpdateFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setUpdateForm({
      status: feedback.status,
      priority: feedback.priority,
      admin_response: feedback.admin_response || '',
      tags: feedback.tags || ''
    });
    setShowUpdateModal(true);
  };

  const submitUpdate = async () => {
    try {
      const res = await API.put(`/api/admin/feedbacks/${selectedFeedback.id}`, {
        feedback_id: selectedFeedback.id,
        ...updateForm
      });
      const { success, message } = res.data;
      if (success) {
        showSuccess('反馈状态更新成功');
        setShowUpdateModal(false);
        loadFeedbacks();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('更新失败');
    }
  };

  const handleAssignFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setAssignForm({
      assigned_to: feedback.assigned_to || ''
    });
    setShowAssignModal(true);
  };

  const submitAssign = async () => {
    try {
      const res = await API.post(`/api/admin/feedbacks/${selectedFeedback.id}/assign`, assignForm);
      const { success, message } = res.data;
      if (success) {
        showSuccess('反馈分配成功');
        setShowAssignModal(false);
        loadFeedbacks();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('分配失败');
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('确定要删除这条反馈记录吗？')) {
      return;
    }

    try {
      const res = await API.delete(`/api/admin/feedbacks/${feedbackId}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess('反馈记录删除成功');
        loadFeedbacks();
      } else {
        showError(message);
      }
    } catch (error) {
      showError('删除失败');
    }
  };

  useEffect(() => {
    loadFeedbacks();
    if (showStatistics) {
      loadStatistics();
    }
  }, [activePage, typeFilter, statusFilter, priorityFilter, assignedToFilter, showStatistics]);

  const getTypeColor = (type) => {
    switch (type) {
      case 1: return 'blue';    // 功能建议
      case 2: return 'orange';  // 小问题
      case 3: return 'red';     // 紧急问题
      default: return 'grey';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 1: return '功能建议';
      case 2: return '小问题';
      case 3: return '紧急问题';
      default: return '未知';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'grey';    // 待处理
      case 2: return 'yellow';  // 处理中
      case 3: return 'green';   // 已解决
      case 4: return 'blue';    // 已关闭
      default: return 'grey';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return '待处理';
      case 2: return '处理中';
      case 3: return '已解决';
      case 4: return '已关闭';
      default: return '未知';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return 'grey';    // 低
      case 2: return 'yellow';  // 中
      case 3: return 'orange';  // 高
      case 4: return 'red';     // 紧急
      default: return 'grey';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return '低';
      case 2: return '中';
      case 3: return '高';
      case 4: return '紧急';
      default: return '未知';
    }
  };

  if (loading && feedbacks.length === 0) {
    return (
      <Segment loading style={{ minHeight: '200px' }}>
        <Header as="h2">
          <Icon name="comments" />
          <Header.Content>
            反馈管理
            <Header.Subheader>管理用户提交的反馈和问题</Header.Subheader>
          </Header.Content>
        </Header>
      </Segment>
    );
  }

  return (
    <div>
      <Header as="h2">
        <Icon name="comments" />
        <Header.Content>
          反馈管理
          <Header.Subheader>管理用户提交的反馈和问题</Header.Subheader>
        </Header.Content>
      </Header>

      {/* 统计概览 */}
      {statistics && showStatistics && (
        <Segment>
          <Header as="h3">
            <Icon name="chart bar" />
            反馈统计概览
          </Header>
          
          <Statistic.Group widths='five'>
            <Statistic>
              <Statistic.Value>{statistics.total_feedbacks}</Statistic.Value>
              <Statistic.Label>总反馈数</Statistic.Label>
            </Statistic>
            <Statistic color='orange'>
              <Statistic.Value>{statistics.pending_feedbacks}</Statistic.Value>
              <Statistic.Label>待处理</Statistic.Label>
            </Statistic>
            <Statistic color='yellow'>
              <Statistic.Value>{statistics.processing_feedbacks}</Statistic.Value>
              <Statistic.Label>处理中</Statistic.Label>
            </Statistic>
            <Statistic color='green'>
              <Statistic.Value>{statistics.resolution_rate.toFixed(1)}%</Statistic.Value>
              <Statistic.Label>解决率</Statistic.Label>
            </Statistic>
            <Statistic color='blue'>
              <Statistic.Value>{statistics.avg_resolution_time.toFixed(1)}h</Statistic.Value>
              <Statistic.Label>平均解决时间</Statistic.Label>
            </Statistic>
          </Statistic.Group>

          <Grid stackable style={{ marginTop: '20px' }}>
            <Grid.Row>
              <Grid.Column width={8}>
                <Card fluid>
                  <Card.Header>反馈类型分布</Card.Header>
                  <Card.Content>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={statistics.feedbacks_by_type}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="count"
                        >
                          {statistics.feedbacks_by_type.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card.Content>
                </Card>
              </Grid.Column>
              
              <Grid.Column width={8}>
                <Card fluid>
                  <Card.Header>每日反馈趋势</Card.Header>
                  <Card.Content>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={statistics.feedbacks_by_day}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Area type="monotone" dataKey="count" stroke="#8884d8" fill="#8884d8" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Card.Content>
                </Card>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      )}

      {/* 筛选和搜索 */}
      <Segment>
        <Form>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>反馈类型</label>
              <Select
                placeholder="选择类型"
                options={typeOptions}
                value={typeFilter}
                onChange={(e, { value }) => setTypeFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>处理状态</label>
              <Select
                placeholder="选择状态"
                options={statusOptions}
                value={statusFilter}
                onChange={(e, { value }) => setStatusFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>优先级</label>
              <Select
                placeholder="选择优先级"
                options={priorityOptions}
                value={priorityFilter}
                onChange={(e, { value }) => setPriorityFilter(value)}
              />
            </Form.Field>
            <Form.Field>
              <label>分配给</label>
              <Select
                placeholder="选择管理员..."
                value={assignedToFilter}
                onChange={(e, { value }) => setAssignedToFilter(value)}
                options={getFilterOptions()}
                clearable
              />
            </Form.Field>
          </Form.Group>
          
          <Form.Group>
            <Form.Field>
              <Button.Group>
                <Button 
                  primary 
                  loading={searchLoading}
                  onClick={handleSearch}
                  icon="search"
                  content="搜索"
                />
                <Button 
                  onClick={() => setShowStatistics(!showStatistics)}
                  icon="chart bar"
                  content={showStatistics ? '隐藏统计' : '显示统计'}
                />
              </Button.Group>
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {/* 反馈列表 */}
      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>标题/类型</Table.HeaderCell>
            <Table.HeaderCell>用户/联系方式</Table.HeaderCell>
            <Table.HeaderCell>状态/优先级</Table.HeaderCell>
            <Table.HeaderCell>分配情况</Table.HeaderCell>
            <Table.HeaderCell>创建时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {feedbacks.map((feedback) => (
            <Table.Row key={feedback.id}>
              <Table.Cell>{feedback.id}</Table.Cell>
              <Table.Cell>
                <div>
                  <strong>{feedback.title}</strong>
                  <br />
                  <Label color={getTypeColor(feedback.type)} size="small">
                    {getTypeText(feedback.type)}
                  </Label>
                  {feedback.execution_id && (
                    <Label size="mini" style={{ marginLeft: '5px' }}>
                      执行#{feedback.execution_id}
                    </Label>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  <Icon name="user" />
                  用户 {feedback.user_id}
                  {feedback.contact_info && (
                    <div style={{ marginTop: '5px', fontSize: '11px' }}>
                      <Icon name="mail" />
                      {feedback.contact_info}
                    </div>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>
                <div>
                  <Label color={getStatusColor(feedback.status)} size="small">
                    {getStatusText(feedback.status)}
                  </Label>
                  <br />
                  <Label color={getPriorityColor(feedback.priority)} size="small" style={{ marginTop: '5px' }}>
                    {getPriorityText(feedback.priority)}
                  </Label>
                </div>
              </Table.Cell>
              <Table.Cell>
                {feedback.assigned_to > 0 ? (
                  <div>
                    <Icon name="user" color="green" />
                    {getAdminDisplayName(feedback.assigned_to)}
                  </div>
                ) : (
                  <div>
                    <Icon name="user outline" color="grey" />
                    未分配
                  </div>
                )}
              </Table.Cell>
              <Table.Cell>
                {timestamp2string(feedback.created_at)}
              </Table.Cell>
              <Table.Cell>
                <Button.Group size="mini">
                  <Button 
                    icon="eye" 
                    onClick={() => showFeedbackDetails(feedback.id)}
                  />
                  <Button 
                    color="blue"
                    icon="edit"
                    onClick={() => handleUpdateFeedback(feedback)}
                  />
                  <Button 
                    color="green"
                    icon="user plus"
                    onClick={() => handleAssignFeedback(feedback)}
                  />
                  <Button 
                    color="red"
                    icon="trash"
                    onClick={() => handleDeleteFeedback(feedback.id)}
                  />
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {/* 分页 */}
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Pagination
          activePage={activePage}
          onPageChange={handlePageChange}
          totalPages={Math.ceil(total / pageSize)}
          ellipsisItem={{ content: <Icon name='ellipsis horizontal' />, icon: true }}
          firstItem={{ content: <Icon name='angle double left' />, icon: true }}
          lastItem={{ content: <Icon name='angle double right' />, icon: true }}
          prevItem={{ content: <Icon name='angle left' />, icon: true }}
          nextItem={{ content: <Icon name='angle right' />, icon: true }}
        />
        <div style={{ marginTop: '10px', color: '#666' }}>
          共 {total} 条记录，第 {activePage} 页
        </div>
      </div>

      {/* 反馈详情模态框 */}
      <Modal open={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} size="large">
        <Modal.Header>
          <Icon name="comments" />
          反馈详情 - ID: {selectedFeedback?.id}
        </Modal.Header>
        <Modal.Content>
          {selectedFeedback && (
            <div>
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Segment>
                      <Header as="h4">基本信息</Header>
                      <p><strong>标题:</strong> {selectedFeedback.title}</p>
                      <p><strong>类型:</strong> 
                        <Label color={getTypeColor(selectedFeedback.type)} style={{ marginLeft: '10px' }}>
                          {getTypeText(selectedFeedback.type)}
                        </Label>
                      </p>
                      <p><strong>优先级:</strong> 
                        <Label color={getPriorityColor(selectedFeedback.priority)} style={{ marginLeft: '10px' }}>
                          {getPriorityText(selectedFeedback.priority)}
                        </Label>
                      </p>
                      <p><strong>状态:</strong> 
                        <Label color={getStatusColor(selectedFeedback.status)} style={{ marginLeft: '10px' }}>
                          {getStatusText(selectedFeedback.status)}
                        </Label>
                      </p>
                      <p><strong>用户ID:</strong> {selectedFeedback.user_id}</p>
                      {selectedFeedback.execution_id && (
                        <p><strong>关联执行:</strong> #{selectedFeedback.execution_id}</p>
                      )}
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Segment>
                      <Header as="h4">联系信息</Header>
                      <p><strong>联系方式:</strong> {selectedFeedback.contact_info || '未提供'}</p>
                      <p><strong>设备信息:</strong> {selectedFeedback.device_info || '未提供'}</p>
                      <p><strong>分配给:</strong> {selectedFeedback.assigned_to > 0 ? getAdminFullInfo(selectedFeedback.assigned_to) : '未分配'}</p>
                      {selectedFeedback.resolved_by > 0 && (
                        <p><strong>解决者:</strong> {getAdminFullInfo(selectedFeedback.resolved_by)}</p>
                      )}
                      {selectedFeedback.resolved_at && (
                        <p><strong>解决时间:</strong> {selectedFeedback.resolved_at}</p>
                      )}
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
                
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Segment>
                      <Header as="h4">问题描述</Header>
                      <pre style={{ 
                        background: '#f4f4f4', 
                        padding: '10px', 
                        borderRadius: '4px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {selectedFeedback.description}
                      </pre>
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
                
                {selectedFeedback.admin_response && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Segment color="green">
                        <Header as="h4" color="green">管理员回复</Header>
                        <pre style={{ 
                          background: '#f0f8f0', 
                          padding: '10px', 
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {selectedFeedback.admin_response}
                        </pre>
                      </Segment>
                    </Grid.Column>
                  </Grid.Row>
                )}

                {selectedFeedback.attachments && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Segment>
                        <Header as="h4">附件</Header>
                        {(() => {
                          try {
                            const attachments = JSON.parse(selectedFeedback.attachments);
                            return attachments.map((attachment, index) => (
                              <div key={index} style={{ marginBottom: '5px' }}>
                                <Icon name="file" />
                                <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                                  {attachment.name}
                                </a>
                              </div>
                            ));
                          } catch (e) {
                            return <p>附件信息格式错误</p>;
                          }
                        })()}
                      </Segment>
                    </Grid.Column>
                  </Grid.Row>
                )}
              </Grid>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowFeedbackModal(false)}>
            <Icon name="close" />
            关闭
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 更新反馈模态框 */}
      <Modal open={showUpdateModal} onClose={() => setShowUpdateModal(false)}>
        <Modal.Header>
          <Icon name="edit" />
          更新反馈状态
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Group widths='equal'>
              <Form.Field>
                <label>处理状态</label>
                <Select
                  options={statusOptions.filter(opt => opt.value !== '')}
                  value={updateForm.status}
                  onChange={(e, { value }) => setUpdateForm({...updateForm, status: value})}
                />
              </Form.Field>
              <Form.Field>
                <label>优先级</label>
                <Select
                  options={priorityOptions.filter(opt => opt.value !== '')}
                  value={updateForm.priority}
                  onChange={(e, { value }) => setUpdateForm({...updateForm, priority: value})}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>管理员回复</label>
              <TextArea
                placeholder="请输入回复内容..."
                value={updateForm.admin_response}
                onChange={(e) => setUpdateForm({...updateForm, admin_response: e.target.value})}
                rows={4}
              />
            </Form.Field>
            <Form.Field>
              <label>标签 (JSON格式)</label>
              <Input
                placeholder='例如: ["bug", "urgent", "frontend"]'
                value={updateForm.tags}
                onChange={(e) => setUpdateForm({...updateForm, tags: e.target.value})}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowUpdateModal(false)}>
            <Icon name="close" />
            取消
          </Button>
          <Button color="blue" onClick={submitUpdate}>
            <Icon name="checkmark" />
            更新
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 分配反馈模态框 */}
      <Modal open={showAssignModal} onClose={() => setShowAssignModal(false)}>
        <Modal.Header>
          <Icon name="user plus" />
          分配反馈
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>分配给管理员</label>
              <Select
                placeholder="选择管理员..."
                value={assignForm.assigned_to}
                onChange={(e, { value }) => setAssignForm({...assignForm, assigned_to: value})}
                options={getAdminOptions(true)}
                search
                clearable
              />
            </Form.Field>
            {selectedFeedback && (
              <Message info>
                <Message.Header>当前分配情况</Message.Header>
                <p>
                  {selectedFeedback.assigned_to > 0 
                    ? `已分配给 ${getAdminFullInfo(selectedFeedback.assigned_to)}`
                    : '未分配'
                  }
                </p>
              </Message>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowAssignModal(false)}>
            <Icon name="close" />
            取消
          </Button>
          <Button color="green" onClick={submitAssign}>
            <Icon name="checkmark" />
            分配
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default FeedbackManagement;
